// Unified API client
// All API calls go through this for consistent error handling

import type { ApiResponse } from '@/types';
import { logger } from '@/utils/logger';
import { getSessionId } from '@/utils/analytics';

interface FetchOptions extends Omit<RequestInit, 'body'> {
  userId?: string;
  body?: unknown;
}

export async function apiRequest<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<ApiResponse<T>> {
  const { userId, body, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  if (userId) {
    (headers as Record<string, string>)['x-user-id'] = userId;
  }

  const sessionId = getSessionId();
  if (sessionId) {
    (headers as Record<string, string>)['x-client-session-id'] = sessionId;
  }

  try {
    const response = await fetch(endpoint, {
      ...fetchOptions,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 404) {
      logger.warn({ userId, area: 'api' }, `404 ${endpoint}`);
      return { success: false, error: 'Not found' };
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        // Use default error message
      }
      logger.error({ userId, area: 'api' }, `${response.status} ${endpoint}`);
      return { success: false, error: errorMessage };
    }

    // Read as text first: a 2xx with an empty body (e.g. DELETE/clearCart) would make
    // response.json() throw "Unexpected end of JSON input" and be reported as a failure
    // even though the call succeeded.
    const responseText = await response.text();
    const data = responseText ? JSON.parse(responseText) : undefined;

    // Handle wrapped response: { success: true, data: ... }
    if (data && typeof data === 'object' && 'success' in data) {
      return data as ApiResponse<T>;
    }

    // Handle direct response (including an empty body → success with no data)
    return { success: true, data: data as T };
  } catch (error) {
    const rawMsg = error instanceof Error ? error.message : 'Unknown error';
    const isNetworkError =
      error instanceof TypeError &&
      /load failed|failed to fetch|networkerror/i.test(rawMsg);
    const errorMsg = isNetworkError
      ? 'Network error. Please check your connection and try again.'
      : rawMsg;
    logger.error({ userId, area: 'api' }, `${endpoint}: ${rawMsg}`);
    return {
      success: false,
      error: errorMsg,
    };
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: FetchOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'POST', body }),

  delete: <T>(endpoint: string, options?: FetchOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};
